import { EventState, FreeCameraMouseInput, Nullable, Observer, PointerEventTypes, PointerInfo, serialize } from '@babylonjs/core';
import { Sphere360Camera } from 'Framework/Components/Controls/XView/Cameras/Sphere360Camera';

export class Camera360MouseInput extends FreeCameraMouseInput {
    public declare camera: Sphere360Camera;

    private _wheel: Nullable<(p: PointerInfo, s: EventState) => void>;
    private _wheelObserver: Nullable<Observer<PointerInfo>>;

    /**
     * Gets or Set the mouse wheel precision or how fast is the camera zooming.
     */
    @serialize()
    public wheelPrecision = 3.0;

    /**
     * wheelDeltaPercentage will be used instead of wheelPrecision if different from 0.
     * It defines the percentage of current camera.radius to use as delta when wheel is used.
     */
    @serialize()
    public wheelDeltaPercentage = 0;

    /**
     * Attach the input controls to a specific dom element to get the input from.
     * @param noPreventDefault Defines whether event caught by the controls should call preventdefault() (https://developer.mozilla.org/en-US/docs/Web/API/Event/preventDefault)
     */
    public attachControl(noPreventDefault?: boolean): void {
        super.attachControl(noPreventDefault);
        this._wheel = (p, s) => {
            //sanity check - this should be a PointerWheel event.
            if (p.type !== PointerEventTypes.POINTERWHEEL) { return; }
            const event = <WheelEvent>p.event;
            let delta = 0;

            let mouseWheelLegacyEvent = event as any;
            if (mouseWheelLegacyEvent.wheelDelta) {
                if (this.wheelDeltaPercentage) {
                    const wheelDelta = (mouseWheelLegacyEvent.wheelDelta * 0.01 * this.wheelDeltaPercentage) * this.camera.fov;
                    if (mouseWheelLegacyEvent.wheelDelta > 0) {
                        delta = wheelDelta / (1.0 + this.wheelDeltaPercentage);
                    } else {
                        delta = wheelDelta * (1.0 + this.wheelDeltaPercentage);
                    }
                } else {
                    delta = mouseWheelLegacyEvent.wheelDelta / (this.wheelPrecision * 40);
                }
            } else {
                let deltaValue = event.deltaY || event.detail;
                delta = -deltaValue / this.wheelPrecision;
            }

            if (delta) {
                this.camera.inertialRadiusOffset += delta;
            }

            if (event.preventDefault) {
                if (!noPreventDefault) {
                    event.preventDefault();
                }
            }
        };

        this._wheelObserver = this.camera.getScene().onPointerObservable.add(this._wheel, PointerEventTypes.POINTERWHEEL);
    }

    public detachControl() {
        const engine = this.camera.getEngine();
        const element = engine.getInputElement();

        if (this._wheelObserver && element) {
            this.camera.getScene().onPointerObservable.remove(this._wheelObserver);
            this._wheelObserver = null;
            this._wheel = null;
        }
    }
}
